import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/auth/useAuth'
import { api } from '@/lib/api'

export interface HouseholdMember {
  age: number
  tobacco: boolean
}

export interface MarketplacePlan {
  external_plan_id: string
  name: string
  issuer_name: string | null
  metal_level: string | null
  plan_type: string | null
  hsa_eligible: boolean
  premium_minor_units: number | null
  premium_with_credit_minor_units: number | null
  deductible_minor_units: number | null
  moop_minor_units: number | null
  currency: string
  source: string
}

export interface MarketplaceSearchResult {
  configured: boolean
  year: number
  state: string
  county: string | null
  plans: MarketplacePlan[]
  unsupported_state?: boolean
}

export interface MarketplaceSearchParams {
  state: string
  zipcode: string
  countyfips: string | null
  people: HouseholdMember[]
  income: number
  market: string
  year: number
}

export interface County {
  fips: string
  name: string
  state: string
  zipcode: string
}

const BASE = '/api/payroll/integrations/benefits/marketplace'

export function useMarketplacePlans(params: MarketplaceSearchParams, enabled: boolean) {
  const { user } = useAuth()
  const tenantId = user?.tenantId
  return useQuery({
    queryKey: ['tenant', tenantId ?? 'anon', 'payroll', 'benefits-marketplace', params],
    queryFn: () => api.post<MarketplaceSearchResult>(`${BASE}/plans`, params),
    enabled: enabled && Boolean(tenantId) && Boolean(params.state),
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function useMarketplaceCounties(zipcode: string, enabled: boolean) {
  const { user } = useAuth()
  const tenantId = user?.tenantId
  return useQuery({
    queryKey: ['tenant', tenantId ?? 'anon', 'payroll', 'benefits-marketplace-counties', zipcode],
    queryFn: () => api.get<{ zipcode: string; counties: County[] }>(`${BASE}/counties?zipcode=${encodeURIComponent(zipcode)}`),
    enabled: enabled && Boolean(tenantId) && /^\d{5}$/.test(zipcode),
    staleTime: 60_000,
  })
}
